import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { StatusBadge } from '../../components/admin/StatusBadge';

interface ExportInvoice {
  id: string;
  invoiceNumber: string;
  buyerId: string | null;
  buyerName: string | null;
  status: string;
  consigneeName: string;
  vesselName: string;
  portOfDischarge: string;
  departureDate: string | null;
  billOfLading: string;
  currency: string;
  incoterms: string;
  totalBags: number;
  totalWeightKg: string;
  totalAmount: string;
  issuedAt: string | null;
  createdAt: string;
}

interface SendModalState {
  isOpen: boolean;
  invoiceId: string;
  invoiceNumber: string;
  buyerName: string;
}

const initialSendModal: SendModalState = { isOpen: false, invoiceId: '', invoiceNumber: '', buyerName: '' };

export const ExportInvoiceList: React.FC = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<ExportInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const [sendModal, setSendModal] = useState<SendModalState>(initialSendModal);
  const [sendEmail, setSendEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');

  const load = () => {
    fetch('/api/admin/export-invoices', { credentials: 'include' })
      .then(r => r.json())
      .then(data => setInvoices(Array.isArray(data) ? data : []))
      .catch(() => setInvoices([]))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const filtered = filter === 'all' ? invoices : invoices.filter(i => i.status === filter);

  const fmtCurrency = (amount: string, cur: string = 'USD') => {
    const symbols: Record<string, string> = { USD: '$', EUR: '\u20AC', GBP: '\u00A3', KES: 'KES ' };
    const sym = symbols[cur] || cur + ' ';
    return `${sym}${parseFloat(amount || '0').toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  };

  const fmtDate = (d: string | null) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const outstanding = invoices.filter(i => i.status !== 'paid').reduce((s, i) => s + parseFloat(i.totalAmount || '0'), 0);
  const totalPaid = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + parseFloat(i.totalAmount || '0'), 0);
  const totalWeight = invoices.reduce((s, i) => s + parseFloat(i.totalWeightKg || '0'), 0);

  const handleDelete = async (id: string, num: string) => {
    if (!confirm(`Delete export invoice ${num}?`)) return;
    const res = await fetch('/api/admin/export-invoices', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ id }),
    });
    if (res.ok) setInvoices(prev => prev.filter(i => i.id !== id));
  };

  const handleViewPdf = (id: string) => {
    window.open(`/api/admin/export-invoices?id=${id}&action=pdf`, '_blank');
  };

  const openSendModal = (inv: ExportInvoice) => {
    setSendModal({ isOpen: true, invoiceId: inv.id, invoiceNumber: inv.invoiceNumber, buyerName: inv.buyerName || inv.consigneeName || 'Unassigned' });
    setSendEmail('');
    setSendError('');
    setSending(false);
  };

  const closeSendModal = () => {
    setSendModal(initialSendModal);
    setSendError('');
  };

  const handleSend = async () => {
    setSending(true);
    setSendError('');
    try {
      const res = await fetch(`/api/admin/export-invoices?id=${sendModal.invoiceId}&action=send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: sendEmail.trim() || undefined }),
      });
      if (res.ok) {
        closeSendModal();
        load();
      } else {
        const data = await res.json().catch(() => ({}));
        setSendError(data.error || 'Failed to send invoice. Please try again.');
      }
    } catch {
      setSendError('Network error. Please check your connection.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A] tracking-tight">Export Invoices</h1>
          <p className="text-sm text-[#A3A3A3] mt-1">Commercial invoices for export shipments</p>
        </div>
        <Link to="/admin/export-invoices/new" className="px-5 py-2.5 bg-[#1A1A1A] text-white rounded-xl text-sm font-medium hover:bg-[#2a2a2a] active:scale-[0.98] transition-all shadow-sm shadow-black/10">
          + New Commercial Invoice
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-5 shadow-sm shadow-black/[0.03]">
          <p className="text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Total Invoices</p>
          <p className="text-2xl font-bold text-[#1A1A1A] mt-1.5 tabular-nums">{invoices.length}</p>
        </div>
        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-5 shadow-sm shadow-black/[0.03]">
          <p className="text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Outstanding</p>
          <p className="text-2xl font-bold text-amber-600 mt-1.5 tabular-nums">${outstanding.toLocaleString('en-US', { minimumFractionDigits: 0 })}</p>
        </div>
        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-5 shadow-sm shadow-black/[0.03]">
          <p className="text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Total Paid</p>
          <p className="text-2xl font-bold text-emerald-600 mt-1.5 tabular-nums">${totalPaid.toLocaleString('en-US', { minimumFractionDigits: 0 })}</p>
        </div>
        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl p-5 shadow-sm shadow-black/[0.03]">
          <p className="text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Total Exported</p>
          <p className="text-2xl font-bold text-[#1A1A1A] mt-1.5 tabular-nums">{totalWeight.toLocaleString()} kg</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-1 bg-[#F5F3F0] rounded-xl p-1 w-fit mb-6">
        {['all', 'draft', 'sent', 'paid'].map(s => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3.5 py-2 text-xs font-medium rounded-lg transition-all ${filter === s ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#6B6B6B] hover:text-[#1A1A1A]'}`}
          >
            {s === 'all' ? `All (${invoices.length})` : `${s.charAt(0).toUpperCase() + s.slice(1)} (${invoices.filter(i => i.status === s).length})`}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => <div key={i} className="h-20 bg-white rounded-2xl animate-pulse shadow-sm" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-[#A3A3A3]">No export invoices yet.</div>
      ) : (
        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl overflow-hidden shadow-sm shadow-black/[0.03]">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#E8E6E2]/60 bg-[#FAFAF8]/50">
                <th className="text-left px-5 py-3.5 text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Invoice</th>
                <th className="text-left px-5 py-3.5 text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Consignee</th>
                <th className="text-left px-5 py-3.5 text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Vessel</th>
                <th className="text-right px-5 py-3.5 text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Bags</th>
                <th className="text-right px-5 py-3.5 text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Weight</th>
                <th className="text-right px-5 py-3.5 text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Amount</th>
                <th className="text-left px-5 py-3.5 text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Status</th>
                <th className="text-right px-5 py-3.5 text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(inv => (
                <tr key={inv.id} className="border-b border-[#E8E6E2]/60 hover:bg-[#FAFAF8]/50 transition-colors">
                  <td className="px-5 py-4">
                    <Link to={`/admin/export-invoices/${inv.id}`} className="font-semibold text-[#1A1A1A] hover:text-[#F05A1E] transition-colors">
                      {inv.invoiceNumber}
                    </Link>
                    <p className="text-xs text-[#A3A3A3] mt-0.5">{fmtDate(inv.departureDate)}</p>
                  </td>
                  <td className="px-5 py-4">
                    <p className="text-sm text-[#1A1A1A]">{inv.buyerName || inv.consigneeName || '-'}</p>
                    {inv.portOfDischarge && <p className="text-xs text-[#A3A3A3] mt-0.5">{inv.portOfDischarge}</p>}
                  </td>
                  <td className="px-5 py-4 text-sm text-[#6B6B6B]">{inv.vesselName || '-'}</td>
                  <td className="px-5 py-4 text-sm text-[#1A1A1A] text-right tabular-nums">{inv.totalBags}</td>
                  <td className="px-5 py-4 text-sm text-[#1A1A1A] text-right tabular-nums">{parseFloat(inv.totalWeightKg || '0').toLocaleString()}</td>
                  <td className="px-5 py-4 text-sm font-semibold text-[#1A1A1A] text-right tabular-nums">{fmtCurrency(inv.totalAmount, inv.currency)}</td>
                  <td className="px-5 py-4"><StatusBadge status={inv.status} /></td>
                  <td className="px-5 py-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => handleViewPdf(inv.id)} className="px-2.5 py-1.5 text-[12px] font-medium text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-[#F0EDE8] rounded-lg transition-colors">PDF</button>
                      <button onClick={() => openSendModal(inv)} className="px-2.5 py-1.5 text-[12px] font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors">Send</button>
                      <button onClick={() => navigate(`/admin/export-invoices/${inv.id}`)} className="px-2.5 py-1.5 text-[12px] font-medium text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-[#F0EDE8] rounded-lg transition-colors">Edit</button>
                      <button onClick={() => handleDelete(inv.id, inv.invoiceNumber)} className="px-2.5 py-1.5 text-[12px] font-medium text-[#C4C4C4] hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {sendModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={closeSendModal} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden animate-scale-in">
            <div className="px-6 py-5 border-b border-[#E8E6E2]/60">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-[#1A1A1A]">Send Commercial Invoice</h2>
                  <p className="text-sm text-[#A3A3A3] mt-0.5">{sendModal.invoiceNumber} &middot; {sendModal.buyerName}</p>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={`/api/admin/export-invoices?id=${sendModal.invoiceId}&action=pdf`}
                    target="_blank"
                    rel="noopener"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#6B6B6B] hover:text-[#1A1A1A] bg-white border border-[#E8E6E2] rounded-lg hover:border-[#1A1A1A]/30 transition-all"
                    title="Preview PDF before sending"
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1 6s1.5-4 5-4 5 4 5 4-1.5 4-5 4-5-4-5-4z" stroke="currentColor" strokeWidth="1.1"/><circle cx="6" cy="6" r="1.5" stroke="currentColor" strokeWidth="1.1"/></svg>
                    PDF
                  </a>
                  <a
                    href={`/api/admin/export-invoices?id=${sendModal.invoiceId}&action=email-preview`}
                    target="_blank"
                    rel="noopener"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#6B6B6B] hover:text-[#1A1A1A] bg-white border border-[#E8E6E2] rounded-lg hover:border-[#1A1A1A]/30 transition-all"
                    title="Preview the email body before sending"
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1.5 2.5h9v7h-9v-7z" stroke="currentColor" strokeWidth="1.1"/><path d="M1.5 3l4.5 3.5L10.5 3" stroke="currentColor" strokeWidth="1.1"/></svg>
                    Email
                  </a>
                </div>
              </div>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-[11px] font-semibold text-[#6B6B6B] uppercase tracking-[0.12em] mb-1.5">Recipient Email(s)</label>
                <input
                  type="text"
                  value={sendEmail}
                  onChange={e => { setSendEmail(e.target.value); setSendError(''); }}
                  placeholder="Leave blank to use buyer's contact email, or enter multiple separated by commas"
                  className="w-full px-4 py-2.5 border border-[#E8E6E2] rounded-xl text-sm text-[#1A1A1A] placeholder-[#C4C4C4] bg-[#FAFAF8] focus:outline-none focus:ring-2 focus:ring-[#1A1A1A]/10 focus:border-[#1A1A1A]/30 focus:bg-white transition-all"
                  autoFocus
                />
                <p className="text-xs text-[#A3A3A3] mt-1.5">Separate multiple addresses with commas.</p>
              </div>
              {sendError && (
                <div className="p-3.5 bg-red-50 border border-red-100 rounded-xl"><p className="text-sm text-red-600 font-medium">{sendError}</p></div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-[#E8E6E2]/60 bg-[#FAFAF8]/50 flex items-center justify-end gap-2">
              <button onClick={closeSendModal} disabled={sending} className="px-4 py-2 text-sm font-medium text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-white rounded-lg border border-[#E8E6E2] transition-all">Cancel</button>
              <button onClick={handleSend} disabled={sending} className="px-5 py-2 text-sm font-medium text-white bg-[#1A1A1A] rounded-xl hover:bg-[#2a2a2a] disabled:opacity-40 transition-all shadow-sm shadow-black/10">
                {sending ? 'Sending...' : 'Send Invoice'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
